import { useCallback, useEffect, useState } from 'react';
// Hooks
import useFetchIntervalData from '../../../../packages/hooks/useFetchIntervalData';
import useSubscriptionClick from '../../../../packages/hooks/useSubscriptionClick';
// Utils
import { getRanking } from '../../../../packages/services/tennis/ranking';
import { normalizeTennisRanking } from '../normalizers/normalizeTennisRanking';
// Types
import { ITennisRankingNormalized } from '../types';
import { RefreshTimes, SDKOptions } from '../../../../packages/types';
import { IStandingsConfig } from '../../../../packages/types/standings';
import { IGeneralSubscriberInfo } from '../../../../packages/constants/subscribeTypes';

interface IUseRankingArgs extends IGeneralSubscriberInfo {
  rankingId: string;
  limit: number;
  offset: number;
  countryId: string;
  dataRefreshTime?: RefreshTimes;
  dataHeaderDefaultOption?: string;
  dataHighlightEntities: string[];
  dataLabels?: IStandingsConfig['dataLabels'];
  dataEntityLinks?: IStandingsConfig['dataEntityLinks'];
  sdkOptions: SDKOptions;
}

export const useRanking = ({
  rankingId,
  limit,
  offset,
  countryId,
  dataRefreshTime,
  dataHighlightEntities,
  dataEntityLinks,
  dataWidgetCuid,
  dataWidgetId,
  dataWidgetSport,
  subscribers,
  sdkOptions,
}: IUseRankingArgs) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [ranking, setRanking] = useState<ITennisRankingNormalized | undefined>(undefined);
  const [hasError, setHasError] = useState<boolean>(false);

  const params = {
    rankingId,
    limit: Number(limit),
    offset: Number(offset),
    countryId,
  };

  const fetchRanking = useCallback(
    (manageLoader: boolean) => {
      if (!rankingId) {
        setRanking(undefined);
        setHasError(true);
        return;
      }

      manageLoader && setIsLoading(true);
      setHasError(false);

      getRanking({ params, sdkOptions })
        ?.then((data) => {
          manageLoader && setIsLoading(false);

          if (data) {
            try {
              const normalizedTennisRanking = normalizeTennisRanking({
                data,
                dataHighlightEntities,
                dataEntityLinks,
              });
              setRanking(normalizedTennisRanking);
            } catch (error) {
              console.error('Error normalizing tennis ranking:', error);
              setRanking(undefined);
              setHasError(true);
            }
          } else {
            setRanking(undefined);
            setHasError(true);
          }
        })
        .catch((error) => {
          console.error('Error fetching ranking:', error);
          manageLoader && setIsLoading(false);
          setRanking(undefined);
          setHasError(true);
        });
    },
    [rankingId, limit, offset, sdkOptions],
  );

  useFetchIntervalData(fetchRanking, dataRefreshTime);

  useEffect(() => {
    fetchRanking(true);
  }, [fetchRanking]);

  return {
    ranking,
    isLoading,
    hasError,
    handleClick: useSubscriptionClick({ dataWidgetId, dataWidgetSport, dataWidgetCuid, subscribers }),
  };
};
